import type { TaxApi } from "../../api/TaxApi";
import type { CatalogFetchOptions } from "../../generated/dto/CatalogFetchOptions";
import type { CatalogItemFetchOptions } from "../../generated/dto/CatalogItemFetchOptions";
import type { CatalogItemStaff } from "../../generated/dto/CatalogItemStaff";
import type { CatalogItemTaxFetchOptions } from "../../generated/dto/CatalogItemTaxFetchOptions";
import type { CatalogItemTaxStaff } from "../../generated/dto/CatalogItemTaxStaff";
import type { CatalogStaff } from "../../generated/dto/CatalogStaff";
import type { CatalogTaxFetchOptions } from "../../generated/dto/CatalogTaxFetchOptions";
import type { CatalogTaxSearchParams } from "../../generated/dto/CatalogTaxSearchParams";
import type { CatalogTaxStaff } from "../../generated/dto/CatalogTaxStaff";
import type { CatalogTaxStaffSearchResult } from "../../generated/dto/CatalogTaxStaffSearchResult";
import type { CreateCatalogItemTax } from "../../generated/dto/CreateCatalogItemTax";
import type { CreateCatalogTax } from "../../generated/dto/CreateCatalogTax";
import type { DeleteCatalogItemTaxStaff } from "../../generated/dto/DeleteCatalogItemTaxStaff";
import type { DeleteCatalogTaxStaff } from "../../generated/dto/DeleteCatalogTaxStaff";
import type { UpdateCatalogItemTax } from "../../generated/dto/UpdateCatalogItemTax";
import type { UpdateCatalogTax } from "../../generated/dto/UpdateCatalogTax";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class TaxFrontendService {
  constructor(private readonly api: TaxApi) {}
  private readonly catalogTaxStaffCache: Map<number, CatalogTaxStaff> = new Map();
  private readonly catalogItemTaxStaffCache: Map<number, CatalogItemTaxStaff> = new Map();
  async getCatalogTaxStaff(id: number, options?: CatalogTaxFetchOptions): Promise<CatalogTaxStaff> {
    const __cached = this.catalogTaxStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogTaxStaff(id, options);
    this.catalogTaxStaffCache.set(id, __value);
    return __value
  }
  async getCatalogItemTaxStaff(id: number, options?: CatalogItemTaxFetchOptions): Promise<CatalogItemTaxStaff> {
    const __cached = this.catalogItemTaxStaffCache.get(id);
    if (__cached !== undefined) {
      return __cached
    }
    const __value = await this.api.getCatalogItemTaxStaff(id, options);
    this.catalogItemTaxStaffCache.set(id, __value);
    return __value
  }
  async getAllCatalogTaxStaffs(options?: CatalogTaxFetchOptions): Promise<Array<CatalogTaxStaff>> {
    return this.api.getAllCatalogTaxStaffs(options)
  }
  async getAllCatalogItemTaxStaffs(options?: CatalogItemTaxFetchOptions): Promise<Array<CatalogItemTaxStaff>> {
    return this.api.getAllCatalogItemTaxStaffs(options)
  }
  async createCatalogTaxStaff(source: CreateCatalogTax): Promise<CatalogTaxStaff> {
    const __result = await this.api.createCatalogTaxStaff(source);
    this.catalogTaxStaffCache.clear();
    return __result
  }
  async createCatalogItemTaxStaff(source: CreateCatalogItemTax): Promise<CatalogItemTaxStaff> {
    const __result = await this.api.createCatalogItemTaxStaff(source);
    this.catalogItemTaxStaffCache.clear();
    return __result
  }
  async updateCatalogTaxStaff(source: UpdateCatalogTax): Promise<CatalogTaxStaff> {
    const __result = await this.api.updateCatalogTaxStaff(source);
    this.catalogTaxStaffCache.clear();
    return __result
  }
  async updateCatalogItemTaxStaff(source: UpdateCatalogItemTax): Promise<CatalogItemTaxStaff> {
    const __result = await this.api.updateCatalogItemTaxStaff(source);
    this.catalogItemTaxStaffCache.clear();
    return __result
  }
  async deleteCatalogTaxStaff(source: DeleteCatalogTaxStaff): Promise<void> {
    const __result = await this.api.deleteCatalogTaxStaff(source);
    this.catalogTaxStaffCache.clear();
    return __result
  }
  async deleteCatalogItemTaxStaff(source: DeleteCatalogItemTaxStaff): Promise<void> {
    const __result = await this.api.deleteCatalogItemTaxStaff(source);
    this.catalogItemTaxStaffCache.clear();
    return __result
  }
  async searchCatalogTaxStaff(params: CatalogTaxSearchParams, page: number, perPage: number): Promise<PaginationResult<CatalogTaxStaffSearchResult>> {
    return this.api.searchCatalogTaxStaff(params, page, perPage)
  }
  async searchCatalogTaxStaffFull(params: CatalogTaxSearchParams, page: number, perPage: number, options?: CatalogTaxFetchOptions): Promise<PaginationResult<CatalogTaxStaff>> {
    return this.api.searchCatalogTaxStaffFull(params, page, perPage, options)
  }
  async getCatalogTaxStaffCatalogId(id: number, options?: CatalogFetchOptions): Promise<CatalogStaff> {
    return this.api.getCatalogTaxStaffCatalogId(id, options)
  }
  async getCatalogItemTaxStaffItemId(id: number, options?: CatalogItemFetchOptions): Promise<CatalogItemStaff> {
    return this.api.getCatalogItemTaxStaffItemId(id, options)
  }
  async getCatalogItemTaxStaffTaxId(id: number, options?: CatalogTaxFetchOptions): Promise<CatalogTaxStaff> {
    return this.api.getCatalogItemTaxStaffTaxId(id, options)
  }
}
